import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, X, MapPin, ChevronRight, ExternalLink, Map as MapIcon,
  ChevronUp, ChevronDown, CheckCircle2, Circle, Loader2, Eye, EyeOff,
  SlidersHorizontal
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

/* ── Geocode cache (city name → [lat, lng]) ── */
const CITY_COORDS = {
  'Bangalore': [12.9716, 77.5946], 'Bengaluru': [12.9716, 77.5946],
  'Mumbai': [19.0760, 72.8777], 'Delhi': [28.6139, 77.2090],
  'New Delhi': [28.6139, 77.2090], 'Hyderabad': [17.3850, 78.4867],
  'Chennai': [13.0827, 80.2707], 'Pune': [18.5204, 73.8567],
  'Kolkata': [22.5726, 88.3639], 'Gurugram': [28.4595, 77.0266]
};

const GEO_CACHE_KEY = 'fairhire_geo_cache_v1';
const readGeoCache = () => {
  try {
    const s = localStorage.getItem(GEO_CACHE_KEY);
    return s ? JSON.parse(s) : {};
  } catch (e) { return {}; }
};
const writeGeoCache = (data) => {
  try { localStorage.setItem(GEO_CACHE_KEY, JSON.stringify(data)); } catch (e) { }
};


const avatarColor = (name = '') => {
  const cols = ['#F59E0B', '#10B981', '#3B82F6', '#8B5CF6', '#EC4899', '#EF4444', '#14B8A6'];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return cols[Math.abs(h) % cols.length];
};

/* Custom map pin icon */
const createPinIcon = (isSelected, color) => {
  const fill = isSelected ? color : '#64748b';
  const size = isSelected ? 32 : 24;
  return L.divIcon({
    className: '',
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
    popupAnchor: [0, -size],
    html: `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="${fill}" opacity="0.9"/>
      <circle cx="12" cy="9" r="3" fill="white" opacity="0.9"/>
    </svg>`,
  });
};

/* ── Map auto-fit component ── */
const MapFitter = ({ locations, selectedLocation, getCoords }) => {
  const map = useMap();
  useEffect(() => {
    if (!locations || locations.length === 0 || !getCoords) return;
    if (selectedLocation) {
      const coords = getCoords(selectedLocation);
      if (coords && coords[0] !== 0) { map.setView(coords, 6, { animate: true }); return; }
    }
    const validCoords = locations.map(l => getCoords(l)).filter(c => c && c[0] !== 0);
    if (validCoords.length === 0) return;
    if (validCoords.length === 1) { map.setView(validCoords[0], 5, { animate: true }); return; }
    const bounds = L.latLngBounds(validCoords);
    map.fitBounds(bounds, { padding: [40, 40], animate: true });
  }, [locations, selectedLocation, map, getCoords]);
  return null;
};

const LocationHiringView = ({
  items, filteredItems, sourceData, companyName, selectedLocation,
  onSelectLocation, accentColor, onCompanyClick, onFindRoles,
  selectedCards, toggleCardSelected, getJobSearchLabel, jobSummaryMap, getOrgId,
  panelCompany, setPanelCompany, orgId, startYear, endYear, mode,
  isLoading = false,
  companyFilter, setCompanyFilter, isCompanyFilterOpen, setIsCompanyFilterOpen,
  jobSummaryLoading, filterMenuRef
}) => {
  const { isDark } = useTheme();
  const [searchQ, setSearchQ] = useState('');
  const [showMap, setShowMap] = useState(true);
  const [dynamicCoords, setDynamicCoords] = useState(readGeoCache);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 860 : false);
  const sidebarRef = useRef(null);
  const companyListRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 860);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!companyListRef.current) return;
    companyListRef.current.scrollTo({ top: 0, behavior: 'auto' });
  }, [selectedLocation, companyFilter]);

  const getCombinedCoords = useCallback((city) => {
    if (!city) return null;
    const c = city.trim();
    if (CITY_COORDS[c]) return CITY_COORDS[c];
    if (dynamicCoords[c]) return dynamicCoords[c];
    // Search for fuzzy match in dynamicCoords
    for (const [k, v] of Object.entries(dynamicCoords)) {
      if (c.toLowerCase().includes(k.toLowerCase()) || k.toLowerCase().includes(c.toLowerCase())) return v;
    }
    for (const [k, v] of Object.entries(CITY_COORDS)) {
      if (c.toLowerCase().includes(k.toLowerCase()) || k.toLowerCase().includes(c.toLowerCase())) return v;
    }
    return null;
  }, [dynamicCoords]);

  const geocodeSingle = async (city) => {
    if (!city || getCombinedCoords(city)) return;
    try {
      const resp = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(city)}&limit=1`, {
        headers: { 'User-Agent': 'FairHire-Insights-App' }
      });
      const data = await resp.json();
      if (data && data[0]) {
        const coords = [parseFloat(data[0].lat), parseFloat(data[0].lon)];
        const updated = { ...readGeoCache(), ...dynamicCoords, [city]: coords };
        setDynamicCoords(updated);
        writeGeoCache(updated);
        return coords;
      }
    } catch (err) {
      console.error('On-demand geocode failed', err);
    }
    return null;
  };

  const allLocations = useMemo(() => {
    return Array.isArray(sourceData?.available_locations) ? sourceData.available_locations : [];
  }, [sourceData]);

  // Background geocoding disabled as requested. Pins are now on-demand based on selection.


  const locationCounts = useMemo(() => {
    const counts = {};
    const locItems = sourceData?.location_hiring || sourceData?.next_destinations || [];
    // We don't have per-location counts from the data, so just count available
    allLocations.forEach(loc => { counts[loc] = counts[loc] || 0; });
    return counts;
  }, [allLocations, sourceData]);

  const filteredLocations = useMemo(() => {
    if (!searchQ.trim()) return allLocations;
    const q = searchQ.toLowerCase();
    return allLocations.filter(l => l.toLowerCase().includes(q));
  }, [allLocations, searchQ]);

  const mappableLocations = useMemo(() => {
    if (!selectedLocation) return [];
    const c = getCombinedCoords(selectedLocation);
    return (c && c[0] !== 0) ? [selectedLocation] : [];
  }, [selectedLocation, getCombinedCoords]);

  const mapCenter = useMemo(() => {
    if (selectedLocation) {
      const c = getCombinedCoords(selectedLocation);
      if (c && c[0] !== 0) return c;
    }
    return [20.5937, 78.9629]; // India default
  }, [selectedLocation, getCombinedCoords]);

  // Colors
  const bgPage = isDark ? '#0d1019' : '#eef2fb';
  const bgSidebar = isDark ? 'rgba(255,255,255,0.03)' : '#ffffff';
  const borderColor = isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0';
  const txtPrimary = isDark ? '#fff' : '#0f172a';
  const txtMuted = isDark ? 'rgba(255,255,255,0.45)' : '#64748b';
  const txtBody = isDark ? 'rgba(255,255,255,0.85)' : '#1e293b';
  const locAccent = accentColor;
  const hoverBg = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(59,130,246,0.04)';
  const selectedBg = isDark ? 'rgba(245,158,11,0.1)' : 'rgba(37,99,235,0.08)';
  const selectedBorder = isDark ? 'rgba(245,158,11,0.3)' : 'rgba(37,99,235,0.2)';

  return (
    <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 0, minHeight: 'calc(100vh - 56px)' }}>
      {/* ── LEFT SIDEBAR ── */}
      {!isMobile && (
        <div
          ref={sidebarRef}
          className="location-sidebar"
          style={{
            width: 280, minWidth: 280, maxWidth: 280, flexShrink: 0,
            borderRight: `1px solid ${borderColor}`,
            background: bgSidebar,
            display: 'flex', flexDirection: 'column',
            position: 'sticky', top: 56, height: 'calc(100vh - 56px)',
            overflow: 'hidden',
          }}
        >
          {/* Sidebar header */}
          <div style={{ padding: '16px 16px 12px', borderBottom: `1px solid ${borderColor}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <MapPin size={15} color={locAccent} />
              <span style={{ fontSize: 12, fontWeight: 800, color: locAccent, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                Locations
              </span>
              <span style={{
                marginLeft: 'auto', fontSize: 11, fontWeight: 700,
                padding: '2px 8px', borderRadius: 999,
                background: isDark ? 'rgba(245,158,11,0.1)' : 'rgba(37,99,235,0.08)',
                color: locAccent,
              }}>
                {allLocations.length}
              </span>
            </div>
            {/* Search */}
            <div style={{ position: 'relative' }}>
              <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: txtMuted }} />
              <input
                value={searchQ}
                onChange={e => setSearchQ(e.target.value)}
                placeholder="Search locations…"
                style={{
                  width: '100%', height: 34, paddingLeft: 32, paddingRight: searchQ ? 30 : 10,
                  borderRadius: 8, fontSize: 12, border: `1px solid ${borderColor}`,
                  background: isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc',
                  color: txtPrimary, outline: 'none', boxSizing: 'border-box',
                }}
              />
              {searchQ && (
                <button onClick={() => setSearchQ('')} style={{
                  position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer', color: txtMuted, padding: 0,
                }}>
                  <X size={12} />
                </button>
              )}
            </div>
          </div>

          {/* "All locations" option */}
          <div style={{ overflowY: 'auto', flex: 1, padding: '4px 0' }}>
            <button
              onClick={() => onSelectLocation('')}
              style={{
                width: '100%', textAlign: 'left', padding: '10px 16px',
                background: !selectedLocation ? selectedBg : 'transparent',
                border: 'none', borderLeft: !selectedLocation ? `3px solid ${locAccent}` : '3px solid transparent',
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10,
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => { if (selectedLocation) e.currentTarget.style.background = hoverBg; }}
              onMouseLeave={e => { if (selectedLocation) e.currentTarget.style.background = 'transparent'; }}
            >
              <MapPin size={14} color={!selectedLocation ? locAccent : txtMuted} />
              <span style={{ fontSize: 13, fontWeight: !selectedLocation ? 700 : 500, color: !selectedLocation ? locAccent : txtBody }}>
                All Locations
              </span>
            </button>

            {/* Location list */}
            {filteredLocations.map(loc => {
              const isSelected = selectedLocation === loc;
              const hasCoords = !!getCombinedCoords(loc);
              return (
                <button
                  key={loc}
                  onClick={async () => {
                    if (!hasCoords) await geocodeSingle(loc);
                    onSelectLocation(loc);
                  }}
                  style={{
                    width: '100%', textAlign: 'left', padding: '9px 16px',
                    background: isSelected ? selectedBg : 'transparent',
                    border: 'none', borderLeft: isSelected ? `3px solid ${locAccent}` : '3px solid transparent',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10,
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = hoverBg; }}
                  onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                >
                  <MapPin size={13} color={isSelected ? locAccent : txtMuted} style={{ flexShrink: 0 }} />
                  <span style={{
                    fontSize: 13, fontWeight: isSelected ? 700 : 500,
                    color: isSelected ? locAccent : txtBody,
                    flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {loc}
                  </span>
                  {hasCoords && (
                    <span style={{
                      width: 6, height: 6, borderRadius: '50%',
                      background: isSelected ? locAccent : (isDark ? 'rgba(255,255,255,0.15)' : '#cbd5e1'),
                      flexShrink: 0,
                    }} />
                  )}
                </button>
              );
            })}
            {filteredLocations.length === 0 && (
              <div style={{ padding: '20px 16px', textAlign: 'center', color: txtMuted, fontSize: 12 }}>
                No locations match "{searchQ}"
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── MAIN CONTENT ── */}
      <div
        className="location-main"
        style={{
          flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column',
          height: 'calc(100vh - 56px)', overflow: 'hidden'
        }}
      >
        {/* Map section */}
        <div style={{ borderBottom: `1px solid ${borderColor}` }}>
          <button
            onClick={() => setShowMap(v => !v)}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 8,
              padding: '10px 24px', background: 'none', border: 'none',
              cursor: 'pointer', color: txtBody, fontSize: 12, fontWeight: 700,
            }}
          >
            {showMap ? <EyeOff size={14} color={txtMuted} /> : <Eye size={14} color={locAccent} />}
            {showMap ? 'Hide Map' : 'Show Map'}
            <MapIcon size={14} color={locAccent} style={{ marginLeft: 4 }} />
            {showMap ? <ChevronUp size={14} color={txtMuted} style={{ marginLeft: 'auto' }} /> : <ChevronDown size={14} color={txtMuted} style={{ marginLeft: 'auto' }} />}
          </button>

          <AnimatePresence>
            {showMap && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: isMobile ? 220 : 280, opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                style={{ overflow: 'hidden' }}
              >
                <div style={{ height: isMobile ? 220 : 280, position: 'relative' }}>
                  <MapContainer
                    center={mapCenter}
                    zoom={4}
                    style={{ height: '100%', width: '100%' }}
                    zoomControl={false}
                    attributionControl={false}
                  >
                    <TileLayer
                      url={isDark
                        ? 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png'
                        : 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png'
                      }
                    />
                    <MapFitter locations={mappableLocations} selectedLocation={selectedLocation} getCoords={getCombinedCoords} />
                    {mappableLocations.map(loc => {
                      const coords = getCombinedCoords(loc);
                      if (!coords) return null;
                      const isSelected = selectedLocation === loc;
                      return (
                        <Marker
                          key={loc}
                          position={coords}
                          icon={createPinIcon(isSelected, locAccent)}
                          eventHandlers={{
                            click: (e) => {
                              L.DomEvent.stopPropagation(e);
                              onSelectLocation(loc);
                            }
                          }}
                        >
                          <Popup>
                            <div style={{ fontFamily: "'Manrope', sans-serif", padding: 2 }}>
                              <strong style={{ fontSize: 13 }}>{loc}</strong>
                            </div>
                          </Popup>
                        </Marker>
                      );
                    })}
                  </MapContainer>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Mobile Location Scroller */}
        {isMobile && (
          <div style={{
            padding: '12px 16px',
            borderBottom: `1px solid ${borderColor}`,
            background: bgSidebar,
            display: 'flex',
            gap: 8,
            overflowX: 'auto',
            whiteSpace: 'nowrap',
            WebkitOverflowScrolling: 'touch',
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
            position: 'sticky',
            top: 56, // Height of InsightsList header
            zIndex: 30,
            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
          }}>
            <button
              onClick={() => onSelectLocation('')}
              style={{
                padding: '8px 16px',
                borderRadius: 99,
                fontSize: 12,
                fontWeight: !selectedLocation ? 700 : 600,
                background: !selectedLocation ? locAccent : (isDark ? 'rgba(255,255,255,0.05)' : '#fff'),
                color: !selectedLocation ? '#fff' : txtBody,
                border: `1px solid ${!selectedLocation ? locAccent : borderColor}`,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                boxShadow: !selectedLocation ? `0 2px 8px ${locAccent}40` : 'none',
              }}
            >
              <MapPin size={12} />
              All Locations
            </button>
            {allLocations.map(loc => {
              const isSelected = selectedLocation === loc;
              return (
                <button
                  key={loc}
                  onClick={() => onSelectLocation(loc)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: 99,
                    fontSize: 12,
                    fontWeight: isSelected ? 700 : 600,
                    background: isSelected ? locAccent : (isDark ? 'rgba(255,255,255,0.05)' : '#fff'),
                    color: isSelected ? '#fff' : txtBody,
                    border: `1px solid ${isSelected ? locAccent : borderColor}`,
                    cursor: 'pointer',
                    boxShadow: isSelected ? `0 2px 8px ${locAccent}40` : 'none',
                  }}
                >
                  {loc}
                </button>
              );
            })}
          </div>
        )}

        {/* Results header */}
        <div style={{
          padding: isMobile ? '16px 20px 12px' : '20px 24px 12px',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'flex-start' : 'center',
          justifyContent: 'space-between',
          gap: isMobile ? 16 : 0
        }}>
          <div>
            <h1 style={{
              fontSize: 'clamp(20px, 2.4vw, 32px)',
              fontWeight: 800,
              fontFamily: "'Playfair Display', serif",
              margin: '0 0 10px',
              color: txtPrimary,
              lineHeight: 1.15,
            }}>
              {selectedLocation
                ? `Companies hiring ${companyName} alumni in ${selectedLocation}`
                : `Where ${companyName} alumni are getting hired`
              }
            </h1>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 10,
              padding: '4px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700,
              background: isDark ? 'rgba(245,158,11,0.1)' : 'rgba(37,99,235,0.08)',
              color: locAccent, border: `1px solid ${isDark ? 'rgba(245,158,11,0.25)' : 'rgba(37,99,235,0.2)'}`,
            }}>
              <MapPin size={12} />
              {selectedLocation || 'All locations'} · {filteredItems.length} companies
            </div>
          </div>

          <div ref={filterMenuRef} style={{ position: 'relative', width: isMobile ? '100%' : 'auto' }}>
            <button
              onClick={() => setIsCompanyFilterOpen((v) => !v)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '8px 12px', borderRadius: 12, fontSize: 12, fontWeight: 700,
                background: isDark ? 'rgba(255,255,255,0.06)' : '#fff',
                border: isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.1)',
                color: txtPrimary, cursor: 'pointer',
                boxShadow: isDark ? 'none' : '0 2px 4px rgba(0,0,0,0.02)',
                width: isMobile ? '100%' : 'auto',
                justifyContent: isMobile ? 'space-between' : 'flex-start',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <SlidersHorizontal size={14} />
                {companyFilter === 'all' ? 'All companies' : companyFilter === 'matches' ? 'Matches profile' : 'Has active jobs'}
              </div>
              <ChevronRight size={14} style={{ transform: isCompanyFilterOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s', marginLeft: 4 }} />
            </button>

            <AnimatePresence>
              {isCompanyFilterOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.95 }}
                  style={{
                    position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 1000,
                    width: 260, padding: 10, borderRadius: 14,
                    background: isDark ? '#1e293b' : '#fff',
                    border: `1px solid ${borderColor}`,
                    boxShadow: '0 20px 50px rgba(0,0,0,0.4)',
                  }}
                >
                  <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.08em', color: txtMuted, marginBottom: 10, paddingLeft: 6 }}>
                    SHOW COMPANIES WHERE
                  </div>
                  {[
                    { key: 'all', label: 'All companies', sub: 'Show everything' },
                    { key: 'matches', label: 'Matching roles', sub: 'Fits your background' },
                    { key: 'has_jobs', label: 'Has active jobs', sub: 'Current job listings' },
                  ].map((opt) => (
                    <button
                      key={opt.key}
                      onClick={() => { setCompanyFilter(opt.key); setIsCompanyFilterOpen(false); }}
                      style={{
                        width: '100%', textAlign: 'left', padding: '10px 12px', borderRadius: 10,
                        background: companyFilter === opt.key ? (isDark ? 'rgba(245,158,11,0.1)' : 'rgba(59,130,246,0.05)') : 'transparent',
                        border: companyFilter === opt.key ? (isDark ? '1px solid rgba(245,158,11,0.25)' : '1px solid rgba(59,130,246,0.2)') : '1px solid transparent',
                        color: companyFilter === opt.key ? accentColor : txtPrimary,
                        cursor: 'pointer', marginBottom: 6
                      }}
                    >
                      <div style={{ fontSize: 12, fontWeight: 700 }}>{opt.label}</div>
                      <div style={{ fontSize: 11, color: txtMuted }}>{opt.sub}</div>
                    </button>
                  ))}
                  {jobSummaryLoading && (
                    <div style={{ fontSize: 11, color: txtMuted, padding: '8px 10px 0', borderTop: `1px solid ${borderColor}`, marginTop: 4 }}>
                      Updating job signals…
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Company list */}
        <div
          ref={companyListRef}
          style={{
            padding: isMobile ? '0 16px 60px' : '0 24px 60px',
            flex: 1,
            position: 'relative',
            minHeight: 0,
            overflowY: isLoading ? 'hidden' : 'auto'
          }}
        >
          {isLoading && (
            <div style={{
              position: 'absolute', top: 0, right: 0, left: 0, bottom: 0,
              background: isDark ? 'rgba(13,16,25,0.7)' : 'rgba(255,255,255,0.7)',
              backdropFilter: 'blur(4px)', zIndex: 100,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              borderRadius: 16
            }}>
              <div style={{ width: 36, height: 36, border: `3px solid ${accentColor}4d`, borderTopColor: accentColor, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
              <p style={{ marginTop: 16, fontSize: 13, fontWeight: 700, color: txtBody, letterSpacing: '0.02em' }}>Updating Results...</p>
            </div>
          )}

          {!isLoading && filteredItems.length === 0 ? (
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              padding: '64px 24px', textAlign: 'center',
              background: isDark ? 'rgba(255,255,255,0.02)' : '#f8faff',
              border: `1px solid ${borderColor}`, borderRadius: 20, marginTop: 12,
            }}>
              <div style={{
                width: 64, height: 64, borderRadius: 16,
                background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: 20
              }}>
                {jobSummaryLoading && companyFilter !== 'all' ? (
                  <Loader2 size={28} style={{ color: accentColor, animation: 'spin 1s linear infinite' }} />
                ) : (
                  <Search size={28} color={txtMuted} />
                )}
              </div>
              <h3 style={{ fontSize: 18, fontWeight: 800, color: txtPrimary, marginBottom: 8, fontFamily: "'Playfair Display', serif" }}>
                {jobSummaryLoading && companyFilter !== 'all'
                  ? 'Filtering companies...'
                  : companyFilter === 'matches'
                    ? 'No matching roles found'
                    : companyFilter === 'has_jobs'
                      ? 'No active jobs found'
                      : 'No companies found'}
              </h3>
              <p style={{ fontSize: 13, color: txtMuted, maxWidth: 340, lineHeight: 1.6, margin: 0 }}>
                {jobSummaryLoading && companyFilter !== 'all'
                  ? 'Hang tight, we are checking job signals and matching roles for your filter.'
                  : companyFilter === 'matches'
                    ? `We couldn't find any companies in ${selectedLocation || 'this area'} with roles that match your background.`
                    : companyFilter === 'has_jobs'
                      ? `There are currently no active job listings from these companies in ${selectedLocation || 'this area'}.`
                      : `No hiring data available for ${selectedLocation || 'this location'} yet. Try exploring a different city.`}
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 8 }}>
              {filteredItems.map((company, idx) => {
                const companyId = company.organization_id || company.organization || idx;
                const isCardSelected = selectedCards.has(companyId);
                const col = avatarColor(company.organization);
                const isPanelOpen = panelCompany?.destOrgId === company.organization_id;

                return (
                  <motion.div
                    key={company.organization_id || idx}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25, delay: idx * 0.03 }}
                    style={{
                      background: isCardSelected
                        ? (isDark ? 'rgba(245,158,11,0.04)' : 'rgba(59,130,246,0.04)')
                        : (isDark ? 'rgba(255,255,255,0.04)' : '#ffffff'),
                      border: `1px solid ${isCardSelected
                        ? (isDark ? 'rgba(245,158,11,0.3)' : 'rgba(59,130,246,0.2)')
                        : (isDark ? 'rgba(255,255,255,0.09)' : '#e2e8f0')}`,
                      borderRadius: 14, padding: isMobile ? '12px 14px' : '14px 16px',
                      display: 'flex',
                      flexDirection: isMobile ? 'column' : 'row',
                      alignItems: isMobile ? 'flex-start' : 'center',
                      gap: isMobile ? 12 : 14,
                      cursor: 'pointer', transition: 'all 0.2s',
                    }}
                    onClick={() => toggleCardSelected(company, idx)}
                    onMouseEnter={e => {
                      if (!isCardSelected) e.currentTarget.style.borderColor = isDark ? 'rgba(245,158,11,0.25)' : 'rgba(59,130,246,0.2)';
                    }}
                    onMouseLeave={e => {
                      if (!isCardSelected) e.currentTarget.style.borderColor = isDark ? 'rgba(255,255,255,0.09)' : '#e2e8f0';
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      flexDirection: isMobile ? 'column' : 'row',
                      alignItems: isMobile ? 'flex-start' : 'center',
                      gap: isMobile ? 10 : 12,
                      flex: 1,
                      minWidth: 0,
                    }}>
                      {/* Select circle */}
                      <div style={{ flexShrink: 0 }}>
                        {isCardSelected
                          ? <CheckCircle2 size={18} color={accentColor} fill={isDark ? 'rgba(245,158,11,0.2)' : 'rgba(37,99,235,0.2)'} />
                          : <Circle size={18} color={isDark ? 'rgba(255,255,255,0.15)' : '#cbd5e1'} />
                        }
                      </div>
                      {/* Avatar */}
                      <div style={{
                        width: 40, height: 40, borderRadius: 10, background: `${col}18`,
                        border: `1.5px solid ${col}35`, display: 'flex', alignItems: 'center',
                        justifyContent: 'center', fontSize: 16, fontWeight: 800, color: col, flexShrink: 0,
                      }}>
                        {(company.organization || '?')[0]?.toUpperCase()}
                      </div>
                      {/* Info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: isMobile ? 14 : 15, fontWeight: 700, color: txtPrimary, margin: 0 }}>
                          {company.organization}
                        </p>
                        <p style={{ fontSize: isMobile ? 11 : 12, color: txtMuted, margin: '3px 0 0' }}>
                          {company.count} {companyName} alumni moved here
                        </p>
                      </div>
                    </div>

                    <div style={{
                      display: 'flex',
                      flexDirection: isMobile ? 'column' : 'row',
                      alignItems: isMobile ? 'flex-start' : 'center',
                      gap: isMobile ? 10 : 20,
                      width: isMobile ? '100%' : 'auto',
                      marginTop: isMobile ? 4 : 0,
                      paddingLeft: 0,
                    }}>
                      {/* Count for mobile */}
                      {isMobile && (
                        <div style={{
                          fontSize: 14,
                          fontWeight: 700,
                          color: locAccent,
                          textAlign: 'left',
                          whiteSpace: 'nowrap',
                          display: 'inline-block',
                          minWidth: 80,
                        }}>
                          {company.count} people
                        </div>
                      )}

                      {/* Count for desktop (Right-aligned group) */}
                      {!isMobile && (
                        <div style={{ textAlign: 'left', flexShrink: 0 }}>
                          <span
                            className="insights-right-label"
                            style={{
                              fontSize: 14,
                              fontWeight: 700,
                              color: locAccent,
                              whiteSpace: 'nowrap',
                              display: 'inline-block',
                              minWidth: 80,
                            }}
                          >
                            {company.count} people
                          </span>
                        </div>
                      )}

                      {/* CTAs */}
                      <div style={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        gap: 8,
                        width: isMobile ? '100%' : 'auto'
                      }}>
                        <button
                          onClick={e => { e.stopPropagation(); onCompanyClick(company); }}
                          style={{
                            width: isMobile ? '100%' : 'auto',
                            padding: '8px 14px', borderRadius: 10, fontSize: 12, fontWeight: 700,
                            background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(59,130,246,0.06)',
                            border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(59,130,246,0.12)'}`,
                            color: isDark ? 'rgba(255,255,255,0.8)' : '#475569',
                            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, whiteSpace: 'nowrap',
                          }}
                        >
                          Find my contact <ChevronRight size={11} />
                        </button>
                        <button
                          onClick={e => { e.stopPropagation(); onFindRoles(company); }}
                          style={{
                            width: isMobile ? '100%' : 'auto',
                            padding: '8px 14px', borderRadius: 10, fontSize: 12, fontWeight: 700,
                            background: isDark ? `linear-gradient(135deg, ${locAccent}, ${locAccent}dd)` : `linear-gradient(135deg, ${locAccent}, ${locAccent}dd)`,
                            color: isDark ? '#1C1917' : '#fff',
                            border: 'none', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, whiteSpace: 'nowrap',
                          }}
                        >
                          Find open roles <ExternalLink size={10} />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Responsive styles */}
      <style>{`
        .leaflet-container { font-family: 'Manrope', sans-serif !important; z-index: 1; }
        .leaflet-popup-content-wrapper { border-radius: 10px !important; box-shadow: 0 4px 16px rgba(0,0,0,0.15) !important; }
        .leaflet-popup-content { margin: 8px 12px !important; }
        .leaflet-popup-tip { display: none !important; }
        .animate-spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @media (max-width: 860px) {
          .location-sidebar { display: none !important; }
          .location-main { width: 100% !important; }
        }
      `}</style>
    </div>
  );
};

export default LocationHiringView;
